<template>
  <div class="relative">
    <div class="relative my-2">
      <span
        class="absolute left-0 inline-block rounded-full h-1.5 min-w-[16px] transition-all"
        :style="{ width: `${progressPercent | 0}%` }"
        :class="progressColorClass"
        v-if="value"
      />
      <span class="block w-full bg-gray-100 rounded-full h-1.5" />
    </div>

    <p
      class="!mt-0 text-left leading-4 text-xs transition-colors"
      :class="{
        'text-red-500': errorMessages.length,
        'text-gray-300': !errorMessages.length,
      }"
    >
      Please use at least 8 characters: one digit, one lower case, one upper
      case and one special character.
    </p>
  </div>
</template>

<script>
import { computed, watch, ref } from '@vue/composition-api'

export default {
  props: {
    value: {
      type: String,
      required: true,
    },
    length: {
      type: Number,
      default: 8,
    },
    uppercase: {
      type: Boolean,
      default: true,
    },
    digits: {
      type: Boolean,
      default: true,
    },
    symbols: {
      type: Boolean,
      default: true,
    },
    showErrors: Boolean,
  },
  setup(props) {
    const matchDigits = /(?=.*\d)/
    const matchLowercase = /(?=.*[a-z])/
    const matchUppercase = /(?=.*[A-Z])/
    const matchSpecialChars = /(?=.*[`!§£~@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])/
    const matchLength = /.{8,}/
    const matchExtraLength = /.{10,}/

    const hasDigits = computed(() => matchDigits.test(props.value))
    const hasLowercase = computed(() => matchLowercase.test(props.value))
    const hasUppercase = computed(() => matchUppercase.test(props.value))
    const hasSpecialChars = computed(() => matchSpecialChars.test(props.value))
    const hasLength = computed(() => matchLength.test(props.value))
    const hasExtraLength = computed(() => matchExtraLength.test(props.value))

    const errorMessages = ref([])
    const getMessages = (value) => {
      errorMessages.value = []

      const _msgs = {
        digits: 'at least one digit',
        lowercase: 'at least one lowercase character',
        uppercase: 'at least one uppercase character',
        special: 'at least one special character',
        length: 'has to be at least 8 characters long',
      }

      if (value) {
        if (!hasDigits.value) errorMessages.value.push(_msgs.digits)
        if (!hasLowercase.value) errorMessages.value.push(_msgs.lowercase)
        if (!hasUppercase.value) errorMessages.value.push(_msgs.uppercase)
        if (!hasSpecialChars.value) errorMessages.value.push(_msgs.special)
        if (!hasLength.value) errorMessages.value.push(_msgs.length)
      }
    }

    const score = computed(() => {
      return (
        (hasDigits.value | 0) +
        (hasLowercase.value | 0) +
        (hasUppercase.value | 0) +
        (hasSpecialChars.value | 0) +
        (hasLength.value | 0) +
        (hasExtraLength.value | 0)
      )
    })

    watch(
      () => props.value,
      (val) => {
        getMessages(val)
        const LOGS_ENABLED = false
        if (LOGS_ENABLED) {
          console.log(score.value)
          console.table(
            [
              {
                type: 'Digits',
                value: hasDigits.value,
              },
              {
                type: 'Lowercase',
                value: hasLowercase.value,
              },
              {
                type: 'Uppercase',
                value: hasUppercase.value,
              },
              {
                type: 'Special characters',
                value: hasSpecialChars.value,
              },
              {
                type: 'Length (8+)',
                value: hasLength.value,
              },
              {
                type: 'Extra Length (10+)',
                value: hasExtraLength.value,
              },
            ],
            ['type', 'value'],
          )
        }
      },
    )

    const progressPercent = computed(() => (score.value / 6) * 100)
    const progressColorClass = computed(() => {
      const value = progressPercent.value

      if (value <= 20) return 'bg-red-700'
      if (value <= 40) return 'bg-yellow-600'
      if (value <= 60) return 'bg-yellow-500'
      if (value <= 80) return 'bg-yellow-400'
      if (value <= 84) return 'bg-green-400'
      return 'bg-green-600'
    })

    return { progressPercent, progressColorClass, errorMessages }
  },
}
</script>
